/**
 * Intake Tab Component
 * Displays mission category questions and attachments
 */

import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import type { MissionDetail } from '@/types/mission';

interface IntakeTabProps {
  mission: MissionDetail;
}

export const IntakeTab: React.FC<IntakeTabProps> = ({ mission }) => {
  const theme = useTheme();

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (mimeType: string) => {
    if (!mimeType) return '📎';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('zip')) return '📦';
    return '📎';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
      {/* Category & Subcategory */}
      {(mission.categoryName || mission.subcategoryName) && (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: theme.typography.subtitle2.fontWeight,
              marginBottom: theme.spacing(1.5),
              color: theme.palette.text.primary,
              fontSize: theme.typography.subtitle2.fontSize,
            }}
          >
            Mission Category
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: theme.typography.subtitle2.fontSize,
            }}
          >
            {mission.categoryName || 'N/A'}
            {mission.subcategoryName && ` > ${mission.subcategoryName}`}
          </Typography>
        </Box>
      )}

      {/* Category Questions */}
      {mission.categoryAnswers && Object.keys(mission.categoryAnswers).length > 0 && (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: theme.typography.subtitle2.fontWeight,
              marginBottom: theme.spacing(2),
              color: theme.palette.text.primary,
              fontSize: theme.typography.subtitle2.fontSize,
            }}
          >
            Category Questions
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            {Object.entries(mission.categoryAnswers).map(([question, answer], index) => (
              <Accordion
                key={question}
                defaultExpanded={index === 0}
                sx={{
                  boxShadow: 'none',
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />}
                  sx={{
                    minHeight: 48,
                    '&.Mui-expanded': {
                      minHeight: 48,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.subtitle2.fontSize,
                    }}
                  >
                    {question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6,
                      fontSize: theme.typography.subtitle2.fontSize,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {typeof answer === 'string' ? answer : JSON.stringify(answer, null, 2)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Attachments */}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            marginBottom: theme.spacing(2),
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
          }}
        >
          Attachments ({mission.attachments?.length || 0})
        </Typography>

        {!mission.attachments || mission.attachments.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No attachments available.
          </Typography>
        ) : (
          <List>
            {mission.attachments.map((attachment, index) => (
              <React.Fragment key={attachment.id}>
                <ListItem
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.customColors.tableRowHover,
                    },
                  }}
                >
                  <Box sx={{ marginRight: theme.spacing(2), fontSize: 24 }}>
                    {getFileIcon(attachment.mimeType)}
                  </Box>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.subtitle2.fontSize,
                        }}
                      >
                        {attachment.fileName}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                          }}
                        >
                          {formatFileSize(attachment.fileSize)} • {attachment.mimeType}
                        </Typography>
                        {attachment.uploadedAtText && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: 'block',
                              fontSize: '0.75rem',
                            }}
                          >
                            Uploaded: {attachment.uploadedAtText}
                            {attachment.uploadedBy && ` by ${attachment.uploadedBy}`}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        if (attachment.url) {
                          window.open(attachment.url, '_blank');
                        }
                      }}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < mission.attachments!.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};
